import React, { useContext, useMemo } from 'react';
import { IModifiersContext, ModifiersContext } from '../../../modifiersState/modifiersContext';
import { UserRoles, IModifiersByRestaurantV2 } from 'types';
import { getDishPrice } from 'utils/dishes';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { Link } from 'react-router-dom';
import Text from 'components/Text/Text';
import { ImgUploaderByPreSignedUrl } from 'components/ImgUploader/ImgUploaderByPreSignedUrl';
import { Badge } from 'components/Badge';
import DataController from 'components/DataController/DataController';
import classNames from 'classnames';
import style from './Item.module.scss';

interface IProps {
	data: IModifiersByRestaurantV2['items'][0];
	index: number;
}

export const Item: React.FC<IProps> = ({ data, index }) => {
	const { setIDForDeletion, setIDForEditModGroupTemplate } = useContext<IModifiersContext>(ModifiersContext);
	const hasEditAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.RESTAURANT_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const modifierRowClasses = classNames(style.modifier, style.row);
	const modifierNameClasses = classNames(style.modifierName);
	const stopStatusRowClasses = classNames(style.stopStatus, style.row);
	const restrictedStatusClasses = classNames(style.restrictedStatus, style.row);
	const forceDisabledStatusClasses = classNames(style.forceDisabledStatus, style.row);
	const priceRowClasses = classNames(style.price, style.row);
	const articleNumberRowClasses = classNames(style.articleNumber, style.row);
	const actionRowClasses = classNames(style.action, style.row);

	const { hasManyPrices, price } = useMemo(() => getDishPrice(data), [data.portionPrices, data.price]);

	const editButtonElement = (
		<div className={style.editItemWrapper}>
			<Link to={`/menu-v2/modifiers/edit?modifierID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editMainInfo" size="s" />
			</Link>
			<Link to={`/menu-v2/modifiers/edit-restaurant-properties?modifierID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editRestaurantsProperties" size="s" />
			</Link>
			<div className={style.linkStyle} onClick={() => setIDForEditModGroupTemplate(data.id)}>
				<Text translationNameSpace="listing" translationKey="editNestedModGroupsTemplate" size="s" />
			</div>
		</div>
	);

	return (
		<div data-test={`Item-${index}`}>
			<div className={style.container}>
				<div className={modifierRowClasses}>
					<div className={style.imgWrapper}>
						<ImgUploaderByPreSignedUrl loaderSize="small" preSignedUrl={data.preSignedUrl} />
					</div>
					<Text
						className={modifierNameClasses}
						translationKey={data.name}
						withTooltip={true}
						data-test="modifierName"
					/>
				</div>
				<div className={stopStatusRowClasses}>
					<Badge color={data.isDisabled ? 'red' : 'purple'}>
						<Text
							translationNameSpace="listing"
							translationKey={data.isDisabled ? 'inStopLabel' : 'availableMaleLabel'}
							asFragment={true}
							data-test="stopStatus"
						/>
					</Badge>
				</div>
				<div className={restrictedStatusClasses}>
					<Badge color={data.isRestricted ? 'red' : 'purple'}>
						<Text
							translationNameSpace="listing"
							translationKey={data.isRestricted ? 'isRestrictedLabel' : 'availableMaleLabel'}
							asFragment={true}
							data-test="restrictedStatus"
						/>
					</Badge>
				</div>
				<div className={forceDisabledStatusClasses}>
					<Badge color={data.forceDisable ? 'red' : 'purple'}>
						<Text
							translationNameSpace="listing"
							translationKey={data.forceDisable ? 'isForceDisabledStatusLabel' : 'availableMaleLabel'}
							asFragment={true}
							data-test="forceDisabledStatus"
						/>
					</Badge>
				</div>
				<Text
					data-test="price"
					className={priceRowClasses}
					translationNameSpace="listing"
					translationKey={hasManyPrices ? 'priceValueWithStartsFrom' : 'priceValue'}
					translationOptions={{
						price: price !== null ? `${data.currencySymbol || ''}${price}` : '-',
					}}
					withTooltip={true}
				/>
				<Text
					className={articleNumberRowClasses}
					translationNameSpace="form"
					translationKey={data.articleNumber}
					withTooltip={true}
					data-test="articleNumber"
				/>
				<div className={actionRowClasses}>
					<DataController
						URLWithIDToInfo={`/menu-v2/modifiers/view?modifierID=${data.id}`}
						customEditButton={hasEditAccess && editButtonElement}
						onClickDelete={() => setIDForDeletion(data.id)}
						size="smallBtn"
					/>
				</div>
			</div>
		</div>
	);
};
